// ─── src/AwardsNominationsView.tsx ───────────────────────────────────────────
import { useEffect, useState } from "react";
import {
  Award,
  Building2,
  Download,
  EyeOff,
  Mail,
  Phone,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import {
  useAwardsNominations,
  useAwardsNominationStats,
  useDeleteAwardsNomination,
} from "./hooks/useAwardsNominations";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { AwardsNomination } from "./api/awardsNominations";

const LIMIT = 20;

// Anonymity is stored verbatim in either language — normalize for filtering
// and the "hidden identity" badge, but display the raw value untouched.
const isAnonymous = (row: AwardsNomination) =>
  row.anonymous === "Yes" || row.anonymous === "نعم";

const CSV_HEADERS = [
  "Category",
  "Nominee Name",
  "Nominee Role",
  "Nominee Email",
  "Nominee Phone",
  "Nominee Links",
  "Why Nominated",
  "Achievements",
  "Standout",
  "Support Needed",
  "Impact Description",
  "Beneficiaries",
  "Reach",
  "Nominator Name",
  "Nominator Email",
  "Nominator Relationship",
  "Anonymous",
  "Submitted At",
];

function exportCsv(rows: AwardsNomination[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.category,
      r.nomineeName,
      r.nomineeRole,
      r.nomineeEmail ?? "",
      r.nomineePhone ?? "",
      r.nomineeLinks ?? "",
      r.nominationWhy,
      r.nominationAchievements,
      r.nominationStandout ?? "",
      r.nominationSupport ?? "",
      r.impactDescription,
      r.impactBeneficiaries ?? "",
      r.impactReach ?? "",
      r.nominatorName,
      r.nominatorEmail,
      r.nominatorRelationship,
      r.anonymous,
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `awards_nominations_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AwardsNominationsView() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AwardsNomination | null>(null);

  const { data: stats } = useAwardsNominationStats();
  const { data: allData } = useAwardsNominations({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useAwardsNominations({ page, limit: LIMIT, search, category });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteAwardsNomination();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const categoryOptions = Object.keys(stats?.byCategory ?? {}).sort();

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total nominations"
          value={stats?.total ?? 0}
          icon={Award}
          accent="bg-brand-orange"
        />
        <StatCard
          label="Anonymous"
          value={stats?.anonymous ?? 0}
          icon={EyeOff}
          accent="bg-brand-teal"
        />
        {categoryOptions.slice(0, 2).map((c) => (
          <StatCard
            key={c}
            label={c}
            value={stats?.byCategory[c] ?? 0}
            icon={Building2}
            accent="bg-brand-teal"
          />
        ))}
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search nominee, nominator, email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load nominations.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          No nominations yet.
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Nominee</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Nominator</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.nomineeName}</div>
                      <div className="text-brand-muted text-xs">{r.nomineeRole}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted">{r.category}</td>
                    <td className="px-4 py-3">
                      {isAnonymous(r) ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-brand-orange/15 text-brand-orange border border-brand-orange/25">
                          <EyeOff size={11} /> Anonymous
                        </span>
                      ) : (
                        <div>
                          <div className="text-white text-sm">{r.nominatorName}</div>
                          <div className="text-brand-muted text-xs">
                            {r.nominatorRelationship}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this nomination?")) deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {selected && (
        <NominationDetailModal
          nomination={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function NominationDetailModal({
  nomination,
  onClose,
}: {
  nomination: AwardsNomination;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const anonymous = isAnonymous(nomination);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/40 animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-brand-border bg-brand-card px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-muted">
              {nomination.category}
            </p>
            <h2 className="mt-1 text-xl font-bold text-white">
              {nomination.nomineeName}
            </h2>
            <p className="text-sm text-brand-muted">{nomination.nomineeRole}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/40"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Legacy email/phone render only on pre-2026 rows that still
              carry them — the current form doesn't collect them. */}
          {(nomination.nomineeEmail ||
            nomination.nomineePhone ||
            nomination.nomineeLinks) && (
            <Section title="Nominee contact" icon={<User size={12} />}>
              <div className="grid gap-4 sm:grid-cols-2">
                {nomination.nomineeEmail && (
                  <Field
                    label="Email (legacy)"
                    value={nomination.nomineeEmail}
                    icon={<Mail size={12} />}
                  />
                )}
                {nomination.nomineePhone && (
                  <Field
                    label="Phone (legacy)"
                    value={nomination.nomineePhone}
                    icon={<Phone size={12} />}
                  />
                )}
                {nomination.nomineeLinks && (
                  <div className="sm:col-span-2">
                    <FieldLabel label="Links" />
                    <p className="text-white text-sm whitespace-pre-wrap break-words">
                      {nomination.nomineeLinks}
                    </p>
                  </div>
                )}
              </div>
            </Section>
          )}

          <Section title="Why this nomination">
            <LongText value={nomination.nominationWhy} />
          </Section>

          <Section title="Key achievements">
            <LongText value={nomination.nominationAchievements} />
          </Section>

          {nomination.nominationStandout && (
            <Section title="What makes them stand out">
              <LongText value={nomination.nominationStandout} />
            </Section>
          )}

          {nomination.nominationSupport && (
            <Section title="Support they've received">
              <LongText value={nomination.nominationSupport} />
            </Section>
          )}

          <Section title="Impact">
            <LongText value={nomination.impactDescription} />
            {(nomination.impactBeneficiaries || nomination.impactReach) && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {nomination.impactBeneficiaries && (
                  <Field label="Beneficiaries" value={nomination.impactBeneficiaries} />
                )}
                {nomination.impactReach && (
                  <Field label="Reach" value={nomination.impactReach} />
                )}
              </div>
            )}
          </Section>

          <Section title="Nominator" icon={<User size={12} />}>
            {anonymous ? (
              <div className="flex items-center gap-2 text-brand-orange">
                <EyeOff size={14} />
                <span className="text-sm">
                  Nominator asked to remain anonymous — identity hidden from the jury.
                </span>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" value={nomination.nominatorName} />
                <Field label="Email" value={nomination.nominatorEmail} />
                <div className="sm:col-span-2">
                  <Field
                    label="Relationship to nominee"
                    value={nomination.nominatorRelationship}
                  />
                </div>
              </div>
            )}
          </Section>

          <p className="text-xs text-brand-muted">
            Submitted {new Date(nomination.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Detail helpers ───────────────────────────────────────────────────────────

function FieldLabel({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-brand-muted mb-1">
      {icon}
      {label}
    </p>
  );
}

function Field({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <FieldLabel label={label} icon={icon} />
      <p className="text-white text-sm break-words">{value}</p>
    </div>
  );
}

function LongText({ value }: { value: string }) {
  return (
    <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
      {value}
    </p>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-muted mb-3">
        {icon}
        {title}
      </h3>
      <div className="bg-brand-navy/50 rounded-xl p-4">{children}</div>
    </div>
  );
}
